/*
 * Full Hero + Text sticky content — scoped per [data-full-hero-root] root.
 * Loaded on the Large Message variation. Works on mobile and desktop.
 * Non-hero: CSS centers initially, JS pins to viewport bottom mid-scroll.
 * Hero ([data-hero-placement="true"]): CSS sticky in the liquid; JS skips.
 * Hooks: [data-full-hero-root], [data-full-hero-sticky-layer],
 *        [data-full-hero-sticky-slot], [data-full-hero-sticky-content].
 */

class FullHeroStickyContent {
  constructor(root) {
    this.root = root;
    this.isHero = root.dataset.heroPlacement === 'true';
    this.entries = [];
    root.querySelectorAll('[data-full-hero-sticky-content]').forEach((content) => {
      const layer = content.closest('[data-full-hero-sticky-layer]');
      const slot = content.closest('[data-full-hero-sticky-slot]');
      if (layer && slot) this.entries.push({ content, layer, slot });
    });
    const styles = getComputedStyle(root);
    const mobileOffset = parseFloat(styles.getPropertyValue('--spacing-2xl'));
    const desktopOffset = parseFloat(styles.getPropertyValue('--spacing-4xl'));
    const breakpoint = parseFloat(styles.getPropertyValue('--breakpoint-lg'));
    this.offsetMobile = Number.isFinite(mobileOffset) && mobileOffset > 0 ? mobileOffset : 32;
    this.offsetDesktop = Number.isFinite(desktopOffset) && desktopOffset > 0 ? desktopOffset : 48;
    this.breakpoint = Number.isFinite(breakpoint) && breakpoint > 0 ? breakpoint : 1024;
    this.frame = null;

    if (!this.entries.length) return;

    this.update = this.update.bind(this);
    this.queueUpdate = this.queueUpdate.bind(this);
    window.addEventListener('scroll', this.queueUpdate, { passive: true });
    window.addEventListener('resize', this.queueUpdate, { passive: true });
    this.update();
  }

  destroy() {
    window.removeEventListener('scroll', this.queueUpdate);
    window.removeEventListener('resize', this.queueUpdate);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.entries.forEach(({ content }) => this.resetContent(content));
  }

  queueUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(this.update);
  }

  get bottomOffset() {
    return window.innerWidth >= this.breakpoint ? this.offsetDesktop : this.offsetMobile;
  }

  update() {
    this.frame = null;
    if (this.isHero) return;

    this.entries.forEach(({ content, layer, slot }) => {
      const layerRect = layer.getBoundingClientRect();
      const slotRect = slot.getBoundingClientRect();
      const contentHeight = content.getBoundingClientRect().height;
      const contentWidth = slotRect.width;

      if (!layerRect.height || !contentHeight || contentHeight >= layerRect.height) {
        this.resetContent(content);
        return;
      }

      // Three reference positions, all measured from the layer's top:
      //  - initialTop:        where CSS positions the content on initial load.
      //  - viewportBottomTop: where a viewport-bottom-anchored content would sit.
      //  - maxTop:            the last layer-relative position before the content
      //                       would clip past the layer's bottom edge.
      const initialTop = slotRect.top - layerRect.top + (slotRect.height - contentHeight) / 2;
      const fixedTop = window.innerHeight - this.bottomOffset - contentHeight;
      const viewportBottomTop = fixedTop - layerRect.top;
      const maxTop = layerRect.height - this.bottomOffset - contentHeight;

      // Non-hero only: above the initial position, let CSS handle it.
      if (viewportBottomTop <= initialTop) {
        this.resetContent(content);
        return;
      }

      // Past the layer end: anchor absolutely to the layer's bottom.
      if (viewportBottomTop >= maxTop) {
        this.applyPosition(content, 'absolute', maxTop, slotRect.left - layerRect.left, contentWidth);
        return;
      }

      // Mid-scroll: pin to the viewport bottom.
      this.applyPosition(content, 'fixed', fixedTop, slotRect.left, contentWidth);
    });
  }

  applyPosition(content, position, top, left, width) {
    content.style.position = position;
    content.style.top = `${top}px`;
    content.style.left = `${left}px`;
    content.style.right = 'auto';
    content.style.width = `${width}px`;
  }

  resetContent(content) {
    content.style.position = '';
    content.style.top = '';
    content.style.left = '';
    content.style.right = '';
    content.style.width = '';
  }
}

function initSections(container = document) {
  const selector = '[data-full-hero-root][data-sticky-enabled="true"]:not([data-full-hero-sticky-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => {
    root.dataset.fullHeroStickyInit = 'true';
    root._stickyInstance = new FullHeroStickyContent(root);
  });
}

initSections();
document.addEventListener('shopify:section:load', (event) => initSections(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector?.('[data-full-hero-root]') || event.target;
  if (root?._stickyInstance) {
    root._stickyInstance.destroy();
    delete root._stickyInstance;
  }
});
